import {BaseElement, BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {IItemUiEnv, installSrcViewFileDrop} from "back/wsp/views/itemMain";
import {IReg, REG} from "lib/commons/registry";
import {IView} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {JWspUriChangeMsg, WSP} from "lib/wsp/wsp";
import {ImgSize, OImgSizeConfig} from "back/wsp/widgets/imgSize";
import {SRC} from "lib/wsp/src";

/**
 * View d'un item sous forme d'une image.
 *
 * Config attributes:
 */
export interface OItemImgInit extends OSkinableInit {
	reg?: IReg<IItemUiEnv>
	transform?: string,
	size: OImgSizeConfig
}

export interface ItemImg extends BaseElement {
	initialize(init: OItemImgInit): this;
}

export class ItemImg extends BaseElement implements IView {
	reg: IReg<IItemUiEnv>;

	img: HTMLImageElement;

	protected _pending: MsgLabel;

	_initialize(init: OItemImgInit) {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		installSrcViewFileDrop(this.reg, this);
		const env = this.reg.env;
		this._pending = sr.appendChild(<c-msg label="..."/>) as MsgLabel;
		this.img = <img src={this.getStreamUrl(this.reg.env, init.transform)} hidden="true"/> as HTMLImageElement;
		this.img.onerror = function (this: HTMLImageElement, ev: Event) {
			DOMSH.findHost<ItemImg>(this).showMainDisplayTag(false);
			DOMSH.findHost<ItemImg>(this)._pending.setCustomMsg("Non disponible", "error");
		};
		this.img.onload = function (this: HTMLImageElement, ev: Event) {
			DOMSH.findHost<ItemImg>(this).showMainDisplayTag(true);
			DOMSH.findHost<ItemImg>(this)._pending.setCustomMsg(null);
		};

		if (init.size) sr.appendChild(<ImgSize î={BASIS.newInit(init.size, this.reg)}>{this.img}</ImgSize>);
		else sr.appendChild(this.img);

		this.showMainDisplayTag(false);

		// Listener sur les changements de la ressource
		env.place.eventsMgr.on("wspUriChange", (msg: JWspUriChangeMsg) => {
			if (SRC.srcRef(env.longDesc) === SRC.srcRef(msg)) {
				this.showMainDisplayTag(false);
				this.img.src = this.getStreamUrl(this.reg.env, init.transform);
			}
		});
	}

	getStreamUrl(env: IItemUiEnv, transform: string) {
		return WSP.getStreamStampedUrl(env.wsp, env.itemType.getMainStreamSrcUri(env.longDesc), env.longDesc.srcDt, transform)
	}

	protected showMainDisplayTag(show: boolean) {
		DOM.setHidden(this._pending, show);
		DOM.setHidden(this.img, !show);
	}
}


REG.reg.registerSkin("wsp-item-img", 1, /* language=CSS */ `
	:host {
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex: 1;
	  flex-direction: column;
	  justify-content: center;
	  padding: 1px;
  }

  :host > img {
	  width: fit-content;
	  height: fit-content;
	  max-width: 100%;
	  max-height: 100%;
	  object-fit: contain;
	  align-self: center;
  }

  img {
	  background-image: linear-gradient(to right, rgba(192, 192, 192, 0.75), rgba(192, 192, 192, 0.75)),
	  linear-gradient(to right, black 50%, white 50%),
	  linear-gradient(to bottom, black 50%, white 50%);
	  background-blend-mode: normal, difference, normal;
	  background-size: 1em 1em;
	}

	wsp-imgsize {
		flex: 1;
	}

  c-msg {
	  flex: 0 0 auto;
  }
`);

customElements.define("wsp-item-img", ItemImg);
